import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import {
    FaTimes, FaEdit, FaTrash, FaTag
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import ofertasService from '../../helpers/ofertasService';

const OfertaDetailModal = ({ isOpen, onClose, oferta, onDelete, onEdit }) => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchOfertaMetrics = React.useCallback(async () => {
        if (!oferta) return;

        setLoading(true);
        try {
            const response = await ofertasService.getOfertaMetrics(oferta._id);
            setMetrics(response);
        } catch (error) {
            console.warn('No se pudieron cargar las métricas:', error);
            setMetrics(null);
        } finally {
            setLoading(false);
        }
    }, [oferta]);

    useEffect(() => {
        if (isOpen && oferta) {
            fetchOfertaMetrics();
        }
    }, [isOpen, oferta, fetchOfertaMetrics]);

    // Usar el servicio para obtener iconos, colores y formateo
    const getTipoIcon = (tipo) => {
        return <FaTag className={ofertasService.getTipoIcon(tipo)} />;
    };

    const getTipoText = (tipo) => {
        const tipos = ofertasService.getTiposOferta();
        const tipoEncontrado = tipos.find(t => t.value === tipo);
        return tipoEncontrado ? tipoEncontrado.label : tipo;
    };

    const getEstadoColor = (estado) => {
        return ofertasService.getEstadoColor(estado);
    };

    const formatDate = (dateString) => {
        return ofertasService.formatDate(dateString);
    };

    const formatValue = (oferta) => {
        return ofertasService.formatOfertaValue(oferta);
    };

    if (!isOpen || !oferta) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center">
                        {getTipoIcon(oferta.tipo)}
                        <div className="ml-3">
                            <h2 className="text-xl font-bold text-gray-800">
                                {oferta.nombre}
                            </h2>
                            <p className="text-sm text-gray-600">
                                {getTipoText(oferta.tipo)}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getEstadoColor(oferta.estado)}`}>
                            {oferta.estado}
                        </span>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Información básica */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Información de la Oferta</h3>
                            <div className="space-y-3">
                                <div>
                                    <span className="text-sm text-gray-600">Descripción:</span>
                                    <p className="text-gray-800 mt-1">
                                        {oferta.descripcion || 'Sin descripción'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600">Valor de la oferta:</span>
                                    <p className="text-gray-800 mt-1 font-semibold text-lg">
                                        {formatValue(oferta)}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600">Objetivo principal:</span>
                                    <p className="text-gray-800 mt-1">
                                        {oferta.objetivoPrincipal?.replace(/_/g, ' ')}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600">Aplicabilidad:</span>
                                    <p className="text-gray-800 mt-1">
                                        {oferta.aplicabilidad || 'Ambos'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Fechas y límites */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Vigencia y Límites</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Fecha de inicio:</span>
                                    <span className="text-sm font-semibold">
                                        {formatDate(oferta.fechaInicio)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Fecha de fin:</span>
                                    <span className="text-sm font-semibold">
                                        {formatDate(oferta.fechaFin)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Usos máximos globales:</span>
                                    <span className="text-sm font-semibold">
                                        {oferta.usosMaximosGlobales || 'Sin límite'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Usos por cliente:</span>
                                    <span className="text-sm font-semibold">
                                        {oferta.usosMaximosPorCliente || 1}
                                    </span>
                                </div>
                                {oferta.importeMinimo > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Importe mínimo:</span>
                                        <span className="text-sm font-semibold">
                                            {oferta.importeMinimo}€
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Métricas */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Métricas</h3>
                        {loading ? (
                            <div className="animate-pulse space-y-3">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="h-8 bg-gray-200 rounded"></div>
                                ))}
                            </div>
                        ) : metrics ? (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Visualizaciones</div>
                                    <div className="text-lg font-semibold text-blue-600">
                                        {metrics.visualizaciones || oferta.visualizaciones || 0}
                                    </div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Usos</div>
                                    <div className="text-lg font-semibold text-green-600">
                                        {metrics.usos || oferta.usos || 0}
                                    </div>
                                </div>
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Tasa de conversión</div>
                                    <div className="text-lg font-semibold text-purple-600">
                                        {metrics.visualizaciones > 0 ? ((metrics.usos / metrics.visualizaciones) * 100).toFixed(1) : 0}%
                                    </div>
                                </div>
                                <div className="bg-orange-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Ventas generadas</div>
                                    <div className="text-lg font-semibold text-orange-600">
                                        {metrics.ventasGeneradas || 0}€
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">No hay métricas disponibles</p>
                        )}
                    </div>

                    {/* Canales de difusión */}
                    {oferta.canalesDifusion && oferta.canalesDifusion.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Canales de Difusión</h3>
                            <div className="flex flex-wrap gap-2">
                                {oferta.canalesDifusion.map(canal => (
                                    <span key={canal} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                                        {canal.replace(/_/g, ' ')}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Mensaje promocional */}
                    {oferta.mensajePromocional && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Mensaje Promocional</h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-gray-800 font-medium">{oferta.mensajePromocional}</p>
                                {oferta.callToAction && (
                                    <p className="text-sm text-gray-600 mt-2">CTA: {oferta.callToAction}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Etiquetas */}
                    {oferta.etiquetas && oferta.etiquetas.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Etiquetas</h3>
                            <div className="flex flex-wrap gap-2">
                                {oferta.etiquetas.map(etiqueta => (
                                    <span key={etiqueta} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                        {etiqueta}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Acciones */}
                    <div className="flex justify-end space-x-3 pt-6 border-t">
                        <button
                            onClick={() => onEdit && onEdit(oferta)}
                            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            <FaEdit className="mr-2" />
                            Editar
                        </button>
                        <button
                            onClick={() => {
                                Swal.fire({
                                    title: '¿Estás seguro?',
                                    text: 'Esta acción no se puede deshacer.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#d33',
                                    confirmButtonText: 'Sí, eliminar',
                                    cancelButtonText: 'Cancelar',
                                }).then((result) => {
                                    if (result.isConfirmed && onDelete) {
                                        onDelete(oferta);
                                    }
                                });
                            }}
                            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                            <FaTrash className="mr-2" />
                            Eliminar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OfertaDetailModal; 